<template>
    <div class="claim-risk-review-page public_box">
        <div class="claim-risk-review-page__header">
            <div>
                <div class="claim-risk-review-page__nav">
                    <el-button size="mini" plain @click="goWorkbench">返回核验工作台</el-button>
                    <el-button size="mini" type="text" @click="goDuplicateReview">切换到重复图片核验</el-button>
                    <el-button size="mini" type="text" @click="goSimilarImageReview">切换到相似图片核验</el-button>
                </div>
                <h2>理算风险案件核验页面</h2>
                <p>集中查看自动理算金额和当前理算金额差异超过 20% 的案件，并复用既有核验操作完成处理。</p>
            </div>
            <div class="claim-risk-review-page__filters">
                <el-switch
                    v-model="filters.onlyPending"
                    active-text="只看待核验"
                    @input="handleOnlyPendingToggle"
                />
                <el-radio-group
                    v-model="filters.reviewStatus"
                    size="mini"
                    :disabled="filters.onlyPending"
                    @input="handleReviewStatusChange"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                    <el-radio-button label="all">全部</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <el-alert
            v-if="errorMessage"
            type="error"
            :closable="false"
            :title="errorMessage"
            class="claim-risk-review-page__alert"
        />

        <el-empty
            v-if="!loading && !records.length"
            description="当前无可核验记录"
        />

        <div v-if="records.length" class="claim-risk-review-page__batch-bar">
            <div class="claim-risk-review-page__batch-summary">
                当前页共 {{ records.length }} 条，可一键批量判断
            </div>
            <div class="claim-risk-review-page__batch-actions">
                <el-radio-group
                    v-model="batchDecision"
                    size="mini"
                    class="claim-risk-review-page__batch-group"
                >
                    <el-radio-button label="pending">待定</el-radio-button>
                    <el-radio-button label="useful">有用</el-radio-button>
                    <el-radio-button label="not-useful">无用</el-radio-button>
                </el-radio-group>
                <el-button
                    size="mini"
                    type="primary"
                    :loading="batchSaving"
                    @click="handleBatchSaveConfirm"
                >
                    当前页批量保存
                </el-button>
            </div>
        </div>

        <div v-loading="loading" class="claim-risk-review-page__list">
            <div
                v-for="record in records"
                :key="record.reviewRecordId"
                class="claim-risk-review-page__card"
            >
                <div class="claim-risk-review-page__main">
                    <div class="claim-risk-review-page__case-row">
                        <span class="claim-risk-review-page__label">案件号</span>
                        <el-button type="text" @click="handleOpenWorkbench(record)">
                            {{ record.caseNo || "-" }}
                        </el-button>
                        <el-tag size="mini" :type="resolveStatusTagType(record.reviewStatus)">
                            {{ resolveStatusLabel(record.reviewStatus) }}
                        </el-tag>
                    </div>
                    <div class="claim-risk-review-page__amount-grid">
                        <div class="claim-risk-review-page__amount-item">
                            <span class="claim-risk-review-page__label">自动理算金额</span>
                            <span>{{ formatAmount(record.autoAmount) }}</span>
                        </div>
                        <div class="claim-risk-review-page__amount-item">
                            <span class="claim-risk-review-page__label">当前医疗赔付金额</span>
                            <span>{{ formatAmount(record.currentAmount) }}</span>
                        </div>
                        <div class="claim-risk-review-page__amount-item">
                            <span class="claim-risk-review-page__label">差异值</span>
                            <span>{{ formatAmount(record.differenceValue) }}</span>
                        </div>
                        <div class="claim-risk-review-page__amount-item">
                            <span class="claim-risk-review-page__label">差异比例</span>
                            <span>{{ formatRatio(record.differenceRatio) }}</span>
                        </div>
                    </div>
                    <div class="claim-risk-review-page__meta">
                        <span>最新原因：{{ record.lastReasonDesc || "无" }}</span>
                        <span v-if="record.reviewUpdatedAt">最近处理：{{ record.reviewUpdatedAt }}</span>
                    </div>
                    <div v-if="historyVisibleMap[record.reviewRecordId]" class="claim-risk-review-page__history">
                        <div class="claim-risk-review-page__history-title">历史变更记录和原因</div>
                        <el-timeline v-if="(historyMap[record.reviewRecordId] || []).length">
                            <el-timeline-item
                                v-for="item in historyMap[record.reviewRecordId]"
                                :key="`${record.reviewRecordId}-${item.historyId}`"
                                :timestamp="item.changedAt"
                            >
                                <div class="claim-risk-review-page__history-reason">
                                    {{ item.reasonDesc || item.reasonCode || "无原因" }}
                                </div>
                                <div class="claim-risk-review-page__history-detail">
                                    {{ buildHistorySummary(item) }}
                                </div>
                            </el-timeline-item>
                        </el-timeline>
                        <el-empty
                            v-else-if="!historyLoadingMap[record.reviewRecordId]"
                            :image-size="60"
                            description="暂无历史"
                        />
                    </div>
                </div>
                <div class="claim-risk-review-page__side">
                    <el-radio-group
                        v-model="draftStatusMap[record.reviewRecordId]"
                        size="mini"
                        class="claim-risk-review-page__decision-group"
                    >
                        <el-radio-button label="pending">待定</el-radio-button>
                        <el-radio-button label="useful">有用</el-radio-button>
                        <el-radio-button label="not-useful">无用</el-radio-button>
                    </el-radio-group>
                    <el-button
                        size="mini"
                        type="primary"
                        :loading="savingRecordId === record.reviewRecordId"
                        @click="handleSave(record)"
                    >
                        保存
                    </el-button>
                    <el-button
                        size="mini"
                        @click="toggleHistory(record)"
                    >
                        {{ historyVisibleMap[record.reviewRecordId] ? "收起历史" : "查看历史" }}
                    </el-button>
                </div>
            </div>
        </div>

        <div class="claim-risk-review-page__pagination">
            <el-pagination
                background
                layout="prev, pager, next"
                :current-page="filters.pageNo"
                :page-size="filters.pageSize"
                :total="total"
                @current-change="handlePageChange"
            />
        </div>
    </div>
</template>

<script>
import { buildDuplicateCaseJumpUrl } from "@/views/calculatorTool/medicalThreeColumn.duplicateCase";
import {
    getClaimRiskReviewHistory,
    getClaimRiskReviewRecords,
    saveClaimRiskReviewBatchDecision,
    saveClaimRiskReviewDecision,
} from "@/api/claimRiskReview";

export default {
    name: "ClaimRiskReviewPage",
    inject: {
        injectedInterfaceUrl: {
            from: "interfaceUrl",
            default: "",
        },
    },
    data() {
        return {
            loading: false,
            savingRecordId: null,
            batchSaving: false,
            records: [],
            total: 0,
            errorMessage: "",
            batchDecision: "useful",
            draftStatusMap: {},
            historyMap: {},
            historyVisibleMap: {},
            historyLoadingMap: {},
            filters: {
                reviewStatus: "pending",
                onlyPending: true,
                pageNo: 1,
                pageSize: 20,
            },
        };
    },
    created() {
        this.hydrateFiltersFromRoute();
        this.fetchRecords();
    },
    methods: {
        getResolvedInterfaceUrl() {
            return this.injectedInterfaceUrl || "";
        },
        goWorkbench() {
            this.$router.push({ path: "/calculatorTool/reviewWorkbench" });
        },
        goDuplicateReview() {
            this.$router.push({ path: "/calculatorTool/duplicateReview" });
        },
        goSimilarImageReview() {
            this.$router.push({ path: "/calculatorTool/similarImageReview" });
        },
        hydrateFiltersFromRoute() {
            const query = (this.$route && this.$route.query) || {};
            const onlyPending = query.onlyPending !== "false";
            this.filters.onlyPending = onlyPending;
            this.filters.reviewStatus = onlyPending ? "pending" : query.reviewStatus || "all";
            this.filters.pageNo = Number(query.pageNo) > 0 ? Number(query.pageNo) : 1;
        },
        syncRouteQuery() {
            if (!this.$router || !this.$route) {
                return;
            }
            this.$router.replace({
                query: {
                    onlyPending: this.filters.onlyPending ? "true" : "false",
                    reviewStatus: this.filters.reviewStatus,
                    pageNo: this.filters.pageNo > 1 ? String(this.filters.pageNo) : undefined,
                },
            }).catch(() => {});
        },
        formatAmount(value) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return Number(value).toFixed(2);
        },
        formatRatio(value) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return `${(Number(value) * 100).toFixed(2)}%`;
        },
        formatHistoryValue(value, formatter) {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            return formatter ? formatter(value) : String(value);
        },
        resolveStatusLabel(status) {
            if (status === null || status === undefined || status === "") {
                return "-";
            }
            const labelMap = {
                pending: "待定",
                useful: "有用",
                "not-useful": "无用",
            };
            return labelMap[status] || String(status);
        },
        resetHistoryState(reviewRecordIds = []) {
            reviewRecordIds.forEach((reviewRecordId) => {
                this.$delete(this.historyMap, reviewRecordId);
                this.$delete(this.historyVisibleMap, reviewRecordId);
                this.$delete(this.historyLoadingMap, reviewRecordId);
            });
        },
        buildHistorySummary(item) {
            const statusPart = `核验状态 ${this.resolveStatusLabel(item.beforeReviewStatus)} → ${this.resolveStatusLabel(item.afterReviewStatus)}`;
            const recordPart = `记录状态 ${this.formatHistoryValue(item.beforeRecordStatus)} → ${this.formatHistoryValue(item.afterRecordStatus)}`;
            const amountPart = `自动理算 ${this.formatHistoryValue(item.beforeAutoAmount, this.formatAmount)} → ${this.formatHistoryValue(item.afterAutoAmount, this.formatAmount)}；当前医疗赔付 ${this.formatHistoryValue(item.beforeCurrentAmount, this.formatAmount)} → ${this.formatHistoryValue(item.afterCurrentAmount, this.formatAmount)}`;
            return `${statusPart}；${recordPart}；${amountPart}`;
        },
        resolveStatusTagType(status) {
            const tagMap = {
                pending: "warning",
                useful: "success",
                "not-useful": "info",
            };
            return tagMap[status] || "warning";
        },
        handleOpenWorkbench(record) {
            const caseNo = String(record?.caseNo || "").trim();
            if (!caseNo) {
                this.$message.warning("当前案件缺少案件号，暂不可跳转");
                return;
            }
            const nextUrl = buildDuplicateCaseJumpUrl("/calculatorTool/medicalThreeColumn", {
                caseNo,
            });
            window.open(nextUrl, "_blank");
        },
        async fetchRecords() {
            this.loading = true;
            this.errorMessage = "";
            try {
                const response = await getClaimRiskReviewRecords(
                    this.getResolvedInterfaceUrl(),
                    {
                        ...this.filters,
                        reviewStatus: this.filters.onlyPending ? "pending" : this.filters.reviewStatus,
                    },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("理算风险核验记录加载失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "理算风险核验记录加载失败");
                }
                const payload = root.data || {};
                this.records = Array.isArray(payload.items) ? payload.items : [];
                this.total = Number(payload.total || 0) || 0;
                this.draftStatusMap = this.records.reduce((acc, item) => {
                    acc[item.reviewRecordId] = item.reviewStatus || "pending";
                    return acc;
                }, {});
            } catch (error) {
                this.records = [];
                this.total = 0;
                this.errorMessage = error && error.message ? error.message : "理算风险核验记录加载失败";
            } finally {
                this.loading = false;
            }
        },
        handleReviewStatusChange(value) {
            this.filters.reviewStatus = value;
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handleOnlyPendingToggle(value) {
            this.filters.onlyPending = Boolean(value);
            this.filters.reviewStatus = this.filters.onlyPending ? "pending" : "all";
            this.filters.pageNo = 1;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        handlePageChange(pageNo) {
            this.filters.pageNo = pageNo;
            this.syncRouteQuery();
            this.fetchRecords();
        },
        async handleSave(record) {
            const decision = this.draftStatusMap[record.reviewRecordId] || "pending";
            this.savingRecordId = record.reviewRecordId;
            try {
                const response = await saveClaimRiskReviewDecision(
                    this.getResolvedInterfaceUrl(),
                    record.reviewRecordId,
                    { decision },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("保存失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "保存失败");
                }
                const latest = root.data || {};
                this.draftStatusMap = {
                    ...this.draftStatusMap,
                    [record.reviewRecordId]: decision,
                };
                this.resetHistoryState([record.reviewRecordId]);
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = this.records.filter((item) => item.reviewRecordId !== record.reviewRecordId);
                    this.total = Math.max(0, Number(this.total || 0) - 1);
                    if (!this.records.length && this.total > 0) {
                        await this.fetchRecords();
                    }
                } else {
                    this.records = this.records.map((item) =>
                        item.reviewRecordId === record.reviewRecordId
                            ? { ...item, ...latest, reviewStatus: decision }
                            : item
                    );
                }
                this.$message.success("判断结果已保存");
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "保存失败");
            } finally {
                this.savingRecordId = null;
            }
        },
        handleBatchSaveConfirm() {
            if (!this.records.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decisionLabelMap = {
                pending: "待定",
                useful: "有用",
                "not-useful": "无用",
            };
            const decisionLabel = decisionLabelMap[this.batchDecision] || this.batchDecision || "待定";
            this.$confirm(
                `确认将当前页 ${this.records.length} 条记录统一标记为「${decisionLabel}」吗？`,
                "批量保存确认",
                {
                    confirmButtonText: "确认保存",
                    cancelButtonText: "取消",
                    type: "warning",
                }
            )
                .then(() => this.handleBatchSave())
                .catch(() => {});
        },
        async handleBatchSave() {
            const reviewRecordIds = this.records
                .map((item) => item && item.reviewRecordId)
                .filter((item) => Number(item) > 0);
            if (!reviewRecordIds.length) {
                this.$message.warning("当前页没有可保存的记录");
                return;
            }
            const decision = this.batchDecision || "pending";
            this.batchSaving = true;
            try {
                const response = await saveClaimRiskReviewBatchDecision(
                    this.getResolvedInterfaceUrl(),
                    {
                        reviewRecordIds,
                        decision,
                    },
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("批量保存失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "批量保存失败");
                }
                this.draftStatusMap = reviewRecordIds.reduce((acc, reviewRecordId) => {
                    acc[reviewRecordId] = decision;
                    return acc;
                }, { ...this.draftStatusMap });
                this.resetHistoryState(reviewRecordIds);
                if (this.filters.onlyPending && decision !== "pending") {
                    this.records = [];
                    this.total = Math.max(0, Number(this.total || 0) - reviewRecordIds.length);
                    await this.fetchRecords();
                } else {
                    await this.fetchRecords();
                }
                const updatedCount = Number(root?.data?.updatedCount || reviewRecordIds.length) || reviewRecordIds.length;
                this.$message.success(`已批量保存 ${updatedCount} 条判断结果`);
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "批量保存失败");
            } finally {
                this.batchSaving = false;
            }
        },
        async toggleHistory(record) {
            const reviewRecordId = record?.reviewRecordId;
            if (!reviewRecordId) {
                return;
            }
            const visible = Boolean(this.historyVisibleMap[reviewRecordId]);
            this.$set(this.historyVisibleMap, reviewRecordId, !visible);
            if (visible || this.historyMap[reviewRecordId]) {
                return;
            }
            this.$set(this.historyLoadingMap, reviewRecordId, true);
            try {
                const response = await getClaimRiskReviewHistory(
                    this.getResolvedInterfaceUrl(),
                    reviewRecordId,
                    this.instance
                );
                if (!response || response.status < 200 || response.status >= 300) {
                    throw new Error("历史轨迹加载失败");
                }
                const root = (response && response.data) || {};
                if (root.code !== 1 && root.code !== undefined) {
                    throw new Error(root.message || "历史轨迹加载失败");
                }
                this.$set(this.historyMap, reviewRecordId, Array.isArray(root.data) ? root.data : []);
            } catch (error) {
                this.$message.error(error && error.message ? error.message : "历史轨迹加载失败");
                this.$set(this.historyMap, reviewRecordId, []);
            } finally {
                this.$set(this.historyLoadingMap, reviewRecordId, false);
            }
        },
    },
};
</script>

<style scoped>
.claim-risk-review-page {
    padding: 20px;
}

.claim-risk-review-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}

.claim-risk-review-page__nav,
.claim-risk-review-page__filters,
.claim-risk-review-page__batch-actions,
.claim-risk-review-page__case-row,
.claim-risk-review-page__side {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.claim-risk-review-page__header h2 {
    margin: 0;
    color: #1f2d3d;
}

.claim-risk-review-page__header p,
.claim-risk-review-page__meta {
    margin: 8px 0 0;
    color: #5e6b80;
    line-height: 1.7;
}

.claim-risk-review-page__history-reason {
    color: #1f2d3d;
    font-weight: 500;
}

.claim-risk-review-page__history-detail {
    margin-top: 4px;
    color: #5e6b80;
    line-height: 1.6;
}

.claim-risk-review-page__alert,
.claim-risk-review-page__batch-bar,
.claim-risk-review-page__pagination {
    margin-top: 16px;
}

.claim-risk-review-page__batch-bar,
.claim-risk-review-page__card {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 12px;
}

.claim-risk-review-page__list {
    display: grid;
    gap: 16px;
    margin-top: 16px;
}

.claim-risk-review-page__main {
    flex: 1;
    min-width: 0;
}

.claim-risk-review-page__side {
    min-width: 180px;
    align-items: flex-end;
    flex-direction: column;
}

.claim-risk-review-page__amount-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-top: 12px;
}

.claim-risk-review-page__amount-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px;
    background: #f7f9fc;
    border-radius: 8px;
}

.claim-risk-review-page__label,
.claim-risk-review-page__history-title {
    color: #909399;
    font-size: 12px;
}

.claim-risk-review-page__history {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #dcdfe6;
}

@media (max-width: 960px) {
    .claim-risk-review-page__header,
    .claim-risk-review-page__card,
    .claim-risk-review-page__batch-bar {
        flex-direction: column;
    }

    .claim-risk-review-page__amount-grid {
        grid-template-columns: 1fr 1fr;
    }

    .claim-risk-review-page__side {
        min-width: 0;
        align-items: flex-start;
    }
}

@media (max-width: 640px) {
    .claim-risk-review-page {
        padding: 16px;
    }

    .claim-risk-review-page__amount-grid {
        grid-template-columns: 1fr;
    }
}
</style>
